import { StrictMode, lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import './index.css'
import App from './App.jsx'
import LoginPage from './pages/LoginPage.jsx'
// Exclusive routes: only one of them (or the app) ever renders in a tab.
const TerminalPage = lazy(() => import('./pages/TerminalPage.jsx'))
const ControlPage  = lazy(() => import('./pages/ControlPage.jsx'))
const SharedPage   = lazy(() => import('./pages/SharedPage.jsx'))
const KidsPage     = lazy(() => import('./extensions/pixel-office/KidsPage.jsx'))
import { useAuth } from './hooks/useAuth.js'
import { ToastProvider } from './contexts/ToastContext.jsx'
import { DataProvider } from './context/DataContext.jsx'
import { SolarProvider } from './context/SolarContext.jsx'
import { VehicleProvider } from './context/VehicleContext.jsx'
import { ApplianceProvider } from './context/ApplianceContext.jsx'
import { LiveDataProvider } from './context/LiveDataContext.jsx'
import { WeatherProvider } from './context/WeatherContext.jsx'
import { SystemProvider } from './context/SystemContext.jsx'
import { ShutterProvider } from './context/ShutterContext.jsx'

// Bypass auth entirely for the ePaper terminal display
const IS_TERMINAL = window.location.pathname.startsWith('/terminal')
// Control page: needs auth but not the full App shell
const IS_CONTROL  = window.location.pathname.startsWith('/control')
// Shared read-only view: same auth, own lightweight shell
const IS_SHARED   = window.location.pathname.startsWith('/shared')
// Emilia's kids dashboard: full viewport for her iPad, no app shell (its effects are position: fixed)
const IS_KIDS     = window.location.pathname.startsWith('/kids')

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,              // flip offline flags immediately — no silent retries
      staleTime: 0,              // always consider data stale so focus-refetch fires
      refetchOnWindowFocus: true,
    },
  },
})

function Root() {
  const { authenticated, loading, user } = useAuth()
  if (loading) return null
  if (!authenticated) return <LoginPage />

  // A kid (Settings › Users › People › Kids page) starts on /kids — Emilia's iPad never
  // shows the house dashboard. /control and /shared stay reachable if shared with her.
  if (user?.kidsRole === 'kid' && user.role !== 'admin' && !IS_KIDS && !IS_CONTROL && !IS_SHARED) {
    window.location.replace('/kids')
    return null
  }

  if (IS_KIDS) {
    return (
      <WeatherProvider>
        <Suspense fallback={null}><KidsPage /></Suspense>
      </WeatherProvider>
    )
  }

  if (IS_CONTROL || IS_SHARED) {
    return (
      <BrowserRouter>
        <ToastProvider>
          <Suspense fallback={null}>{IS_CONTROL ? <ControlPage /> : <SharedPage />}</Suspense>
        </ToastProvider>
      </BrowserRouter>
    )
  }

  return (
    <BrowserRouter>
      <ToastProvider>
        <LiveDataProvider>
          <WeatherProvider>
            <SystemProvider>
              <ShutterProvider>
                <DataProvider>
                  <SolarProvider>
                    <VehicleProvider>
                      <ApplianceProvider>
                        <App />
                      </ApplianceProvider>
                    </VehicleProvider>
                  </SolarProvider>
                </DataProvider>
              </ShutterProvider>
            </SystemProvider>
          </WeatherProvider>
        </LiveDataProvider>
      </ToastProvider>
    </BrowserRouter>
  )
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {IS_TERMINAL ? <Suspense fallback={null}><TerminalPage /></Suspense> : <Root />}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />}
    </QueryClientProvider>
  </StrictMode>,
)
